/* eslint-disable operator-linebreak, react/jsx-one-expression-per-line */

import React, { useEffect, useState } from 'react';
import { useHCaptchaContext, usePaymentContext } from '@fff-web/fff-payments';
import { ApplePayRadio, PayPalRadio } from '@fff-web/fff-ui-shared/Icon';

import Link from '@fff-web/fff-ui-shared/Link';

import { PaymentType } from '../../../../utils/Data/registerConnectorTypes';
import {
  FABFITFUN_MEMBERSHIP_TERMS,
  FABFITFUN_SUBSCRIBABLE_TERMS,
  TERMS_OF_USE_AND_SALE,
} from '../../../../utils/legalLinkConstants';
import { useGTBCheckoutContext } from '../../ContextProviders/GTBContext';
import { usePublicShopContext } from '../../ContextProviders/PublicShopContext';
import EXPERIENCE_FLOW from '../../../../utils/constants/experienceFlow';
import { useSharedContext } from '../../ContextProviders/SharedContext';
import { checkShippingRestrictions } from '../../../../components/Checkout/utils/checkoutUtils';
import { isValidEmail } from '../../../../components/AccountForm/utilities/accountFormUtilities';
import { validatePassword } from '../../../../components/AccountForm/utilities/passwordValidation';
import checkoutSecureLogo from '../../../../components/PaymentInfo/assets/img/checkout-secure-logo.png';
import styles from './orderButton.module.scss';

const CHECK_OUT_WITH = 'Check out with';
const UK_COUNTRY = 'GB';

const Lock = () => <i className="fal fa-lock" />;

const OrderButton = () => {
  const { billingMethod, handlePayPal, handleApplePay } = usePaymentContext();
  const { setExecuteHCaptcha, executeHCaptcha } = useHCaptchaContext();
  const {
    isRecurlyFormValid,
    prospect,
    experienceFlow,
    loading,
    submitText,
    previewError,
    setLoading,
    setSubmitText,
  } = useSharedContext();
  const { hasRefill } = usePublicShopContext();
  const resetHCaptcha = () => {
    setExecuteHCaptcha(false);
  };
  const {
    email,
    password,
    address: { shipCountry, shipState, shipZipCode },
  } = prospect;
  const isPublicShop = experienceFlow === EXPERIENCE_FLOW.publicShop;

  const useContext = isPublicShop
    ? usePublicShopContext
    : useGTBCheckoutContext;
  const { choices } = useContext();

  const orderButton = isPublicShop ? 'Place Order' : 'Order the Box';

  const [billingMethodText, setBillingMethodText] = useState(orderButton);
  const [billingIcon, setBillingIcon] = useState(<Lock />);
  const [buttonClass, setButtonClass] = useState(styles.orderButton);

  const [disableOrderButton, setDisableOrderButton] = useState(true);

  const stateValid = shipCountry === UK_COUNTRY ? true : !!shipState;
  const inputsValid =
    isValidEmail(email.trim()) &&
    validatePassword(password.trim()) &&
    !!shipCountry &&
    stateValid &&
    !!shipZipCode &&
    !previewError;

  useEffect(() => {
    if (checkShippingRestrictions(choices, shipCountry)) {
      setDisableOrderButton(true);
    } else if (billingMethod.toLowerCase() !== PaymentType.CREDIT_CARD) {
      setDisableOrderButton(!inputsValid);
    } else {
      setDisableOrderButton(!inputsValid);
    }
  }, [
    billingMethod,
    isRecurlyFormValid,
    inputsValid,
    previewError,
    shipCountry,
  ]);

  useEffect(() => {
    switch (billingMethod.toLowerCase()) {
      case PaymentType.CREDIT_CARD:
        setBillingMethodText(orderButton);
        setBillingIcon(<Lock />);
        setSubmitText(orderButton);
        setButtonClass(styles.orderButton);
        break;
      case PaymentType.PAYPAL:
        setBillingMethodText(CHECK_OUT_WITH);
        setBillingIcon(<PayPalRadio />);
        setSubmitText(CHECK_OUT_WITH);
        setButtonClass(styles.orderButtonPayPal);
        break;
      case PaymentType.APPLE_PAY:
        setBillingMethodText(CHECK_OUT_WITH);
        setBillingIcon(<ApplePayRadio />);
        setSubmitText(CHECK_OUT_WITH);
        setButtonClass(styles.orderButtonApplePay);
        break;
      default:
        setBillingMethodText(orderButton);
        setBillingIcon(<Lock />);
        setSubmitText(orderButton);
        setButtonClass(styles.orderButton);
    }
  }, [billingMethod]);

  const handleHCaptcha = () => {
    if (executeHCaptcha) resetHCaptcha();
    setTimeout(() => {
      setExecuteHCaptcha(true);
    }, 200);
  };

  const handleSubmit = (paymentType: string) => {
    setLoading(true);
    switch (paymentType) {
      case 'CREDIT_CARD':
        handleHCaptcha();
        break;
      case 'PAYPAL':
        handleHCaptcha();
        handlePayPal();
        break;
      case 'APPLE_PAY':
        handleHCaptcha();
        handleApplePay();
        break;
      default:
        break;
    }
  };

  const renderDisclaimer = () => {
    const disclaimerItems = [];

    if (isPublicShop) {
      disclaimerItems.push(
        <Link
          href={FABFITFUN_MEMBERSHIP_TERMS}
          text="FabFitFun Membership Terms"
          fontSize={12}
        />,
      );
    }

    if (hasRefill) {
      disclaimerItems.push(
        <Link
          href={FABFITFUN_SUBSCRIBABLE_TERMS}
          text="Subscriptions Terms and Conditions"
          fontSize={12}
        />,
      );
    }

    return (
      <>
        By clicking &quot;{billingMethodText}&quot;, you agree to our
        {' '}
        <Link
          href={TERMS_OF_USE_AND_SALE}
          text="Terms of Use and Sale"
          fontSize={12}
        />
        {disclaimerItems.map((item, index) => (
          <>
            {index === disclaimerItems.length - 1 ? ' and ' : ', '}
            {item}
          </>
        ))}
        .
      </>
    );
  };

  return (
    <div className={styles.orderButtonContainer}>
      <div className={styles.orderButtonLegalTextContainer}>
        <p className={styles.orderButtonLegalText}>
          {renderDisclaimer()}
        </p>
      </div>
      <button
        type="button"
        className={buttonClass}
        disabled={disableOrderButton || loading}
        onClick={() => handleSubmit(billingMethod)}
      >
        <span className={styles.iconContainer}>{billingIcon}</span>
        {submitText}
      </button>
      <div className={styles.checkoutLogoContainer}>
        <img
          src={checkoutSecureLogo}
          alt="Checkout secure"
          className={styles.checkoutSecureLogo}
        />
      </div>
    </div>
  );
};

export default OrderButton;
